interface BaseConfigurationMetadata {
    title: string;
    value: string;
    description: string;
    type: string;
    default?: any;
    restart_required?: boolean;
}

interface StringConfigurationMetadata extends BaseConfigurationMetadata {
    type: 'string';
    min_length?: number;
    max_length?: number;
    options?: string[];
}

interface NumberConfigurationMetadata extends BaseConfigurationMetadata {
    type: 'number';
    min_value?: number;
    max_value?: number;
}

interface BooleanConfigurationMetadata extends BaseConfigurationMetadata {
    type: 'boolean';
}

type ConfigurationMetadata = StringConfigurationMetadata | NumberConfigurationMetadata | BooleanConfigurationMetadata;

// Platform default configurations - should match backend configuration.py
const PLATFORM_CONFIGURATIONS: Record<string, ConfigurationMetadata> = {
    'SITENAME': {
        title: 'Application name',
        value: 'Leadstream',
        description: 'The name of the application',
        type: 'string',
        min_length: 3,
        max_length: 255,
    },
    'FS_SESSION_EXPIRY_WARNING_TIME_IN_SECONDS': {
        title: 'Session expiry warning time in seconds',
        value: '300',
        description: 'The time in seconds before the session expires and the user is prompted to extend the session',
        type: 'number',
        min_value: 60,
        max_value: 86400,
    },
    'PERIOD_DEFAULT_LENGTH': {
        title: 'Default period length',
        value: 'this_month',
        description: 'The default length of the period',
        type: 'string',
        options: ['this_month', 'this_week', 'this_day'],
        default: 'this_month',
    },
    'MAX_CHART_LEGEND_LABELS_SHOWN': {
        title: 'Maximum chart legend labels',
        value: '40',
        description: 'The maximum number of labels shown in the chart legend',
        type: 'number',
        min_value: 1,
        max_value: 100,
    },
    'MAX_DATE_RANGE_DATA': {
        title: 'Maximum date range days',
        value: '730',
        description: 'The maximum number of days of data shown in the date range picker',
        type: 'number',
        min_value: 30,
        max_value: 365,
    },
    'WORDCLOUD_DEFAULT_MOST_COMMON_WORDS': {
        title: 'Word cloud default words',
        value: '200',
        description: 'The default number of most common words shown in the word cloud',
        type: 'number',
        min_value: 50,
        max_value: 200,
    },
    'RESERVED_EVALUATION_KEYS': {
        title: 'Reserved evaluation keys',
        value: 'false',
        description: 'Whether or not to reverse evaluation keys',
        type: 'boolean',
        default: false,
        restart_required: true,
    },
    'COMPRESS_AUDIO_BITRATE': {
        title: 'Audio compression bitrate',
        value: '8k',
        description: 'The bitrate of the compressed audio',
        type: 'string',
        options: ['8k', '16k'],
        default: '8k',
        restart_required: true,
    },
    'GLOBAL_FONT_SIZE': {
        title: 'Global font size',
        value: 'medium',
        description: 'The global font size for all users (xsmall, small, medium, large, xlarge)',
        type: 'string',
        options: ['xsmall', 'small', 'medium', 'large', 'xlarge'],
        default: 'medium',
    }
};

export interface ConfigurationValidationResult {
    isValid: boolean;
    errorMessage: string;
}

export const validateConfigurationValue = (key: string, value: string): ConfigurationValidationResult => {
    const config = PLATFORM_CONFIGURATIONS[key];

    if (!config) {
        return {
            isValid: false,
            errorMessage: `Unknown configuration key: ${key}`
        };
    }

    const configType = config.type || 'string';

    try {
        // Type validation and conversion
        if (configType === 'number') {
            const numericValue = parseFloat(value);

            if (isNaN(numericValue)) {
                return {
                    isValid: false,
                    errorMessage: `Value must be a valid number, got: ${value}`
                };
            }

            // Range validation for number configs
            const numberConfig = config as NumberConfigurationMetadata;
            if (numberConfig.min_value !== undefined && numericValue < numberConfig.min_value) {
                return {
                    isValid: false,
                    errorMessage: `Value must be at least ${numberConfig.min_value}, got: ${numericValue}`
                };
            }

            if (numberConfig.max_value !== undefined && numericValue > numberConfig.max_value) {
                return {
                    isValid: false,
                    errorMessage: `Value must be at most ${numberConfig.max_value}, got: ${numericValue}`
                };
            }

        } else if (configType === 'boolean') {
            const lowerValue = value.toLowerCase();
            if (!['true', 'false', '1', '0', 'yes', 'no'].includes(lowerValue)) {
                return {
                    isValid: false,
                    errorMessage: `Value must be a boolean (true/false), got: ${value}`
                };
            }

        } else if (configType === 'string') {
            // Length and options validation for string configs
            const stringConfig = config as StringConfigurationMetadata;

            if (stringConfig.min_length !== undefined && value.length < stringConfig.min_length) {
                return {
                    isValid: false,
                    errorMessage: `Value must be at least ${stringConfig.min_length} characters long, got: ${value.length}`
                };
            }

            if (stringConfig.max_length !== undefined && value.length > stringConfig.max_length) {
                return {
                    isValid: false,
                    errorMessage: `Value must be at most ${stringConfig.max_length} characters long, got: ${value.length}`
                };
            }

            // Options validation
            if (stringConfig.options && !stringConfig.options.includes(value)) {
                return {
                    isValid: false,
                    errorMessage: `Value must be one of [${stringConfig.options.join(', ')}], got: ${value}`
                };
            }
        }

        return {
            isValid: true,
            errorMessage: ''
        };

    } catch (error) {
        return {
            isValid: false,
            errorMessage: `Validation error: ${error}`
        };
    }
};

export const getConfigurationMetadata = (key: string): ConfigurationMetadata | null => {
    return PLATFORM_CONFIGURATIONS[key] || null;
};

export const getConfigurationOptions = (key: string): string[] | null => {
    const config = PLATFORM_CONFIGURATIONS[key];
    if (config?.type === 'string') {
        const stringConfig = config as StringConfigurationMetadata;
        return stringConfig.options || null;
    }
    return null;
};

export const isConfigurationBoolean = (key: string): boolean => {
    const config = PLATFORM_CONFIGURATIONS[key];
    return config?.type === 'boolean';
};

export const isConfigurationNumber = (key: string): boolean => {
    const config = PLATFORM_CONFIGURATIONS[key];
    return config?.type === 'number';
};

export const getAllConfigurationKeys = (): string[] => {
    return Object.keys(PLATFORM_CONFIGURATIONS);
};
